// src/components/anlagen/AnlagenRow.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Input,
  IconButton,
  Tooltip
} from "@chakra-ui/react";
import { Group } from "@chakra-ui/react";
import { useColorModeValue } from "../ui/color-mode";
import { Trash, Pencil, Save, Search } from "lucide-react";

export default function AnlagenRow({ anlage, user, onDelete, onUpdate, onView, onEdit, }) {
  const [editData, setEditData] = useState({
    projektNummer: anlage?.projektNummer ?? "",
    anlagenName: anlage?.anlagenName ?? "",
    ort: anlage?.ort ?? "",
    anlagenGroesse: anlage?.anlagenGroesse ?? "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const firstInputRef = useRef(null);

  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  useEffect(() => {
    if (isEditing && firstInputRef.current) firstInputRef.current.focus();
  }, [isEditing]);

  const handleChange = (e) =>
    setEditData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSave = () => {
    const payload = {
      projektNummer: editData.projektNummer,
      anlagenName: editData.anlagenName,
      ort: editData.ort,
      anlagenGroesse: editData.anlagenGroesse,
    };

    onUpdate?.(anlage?.id, payload);
    setIsEditing(false);
  };

  const editableFields = ["projektNummer", "anlagenName", "ort", "anlagenGroesse"];

  const renderCellText = (val) =>
    val == null ? "-" : typeof val === "object" ? JSON.stringify(val) : String(val);

  return (
    <Box as="tr" _hover={{ bg: hoverBg }}>
      {editableFields.map((key) => (
        <Box
          as="td"
          key={key}
          borderBottom="1px solid"
          borderColor={borderColor}
          py="8px"
          px="12px"
        >
          {isEditing ? (
            <Input
              size="sm"
              name={key}
              value={editData[key]}
              onChange={handleChange}
            />
          ) : (
            <Box as="span">{renderCellText(editData[key])}</Box>
          )}
        </Box>
      ))}

      <Box
        as="td"
        borderBottom="1px solid"
        borderColor={borderColor}
        py="8px"
        px="12px"
      >
        <Box display="flex" justifyContent="flex-start">
          <Group size="sm" spacing={2}>
            {/* Anzeigen */}
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <IconButton aria-label="Anlage anzeigen" onClick={() => onView?.(anlage)} colorPalette="blue">
                  <Search size={18} />
                </IconButton>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content>Anlage anzeigen</Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>

            {/* Speichern / Bearbeiten */}
            {isEditing ? (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <IconButton aria-label="Änderungen speichern" onClick={handleSave} colorPalette="green">
                    <Save size={18} />
                  </IconButton>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>Änderungen speichern</Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            ) : (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <IconButton
                    aria-label="Anlage bearbeiten"
                    onClick={() => onEdit?.(anlage)}
                    colorPalette="yellow"
                  >
                    <Pencil size={18} />
                  </IconButton>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>Bearbeiten</Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            )}

            {/* Löschen (nur Admin) */}
            {user?.role === "ROLE_ADMIN" && (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <IconButton aria-label="Anlage löschen" onClick={() => onDelete?.(anlage?.id)} colorPalette="red">
                    <Trash size={18} />
                  </IconButton>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>Anlage löschen</Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            )}
          </Group>
        </Box>
      </Box>
    </Box>
  );
}